import { useState, useRef, useEffect } from 'react'
import { useAuthStore } from '../../store/authStore'
import api from '../../services/api'
import {
  Send,
  Sparkles,
  Loader2,
  AlertCircle,
  BookOpen,
  Users,
  FileCheck,
  Calendar,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  Brain,
} from 'lucide-react'

const QUICK_ACTIONS = [
  { label: "Today's teaching briefing", message: "Give me my daily teaching briefing.", icon: Calendar },
  { label: 'Prepare this lesson', message: "Help me prepare to teach this lesson.", icon: BookOpen },
  { label: 'Students needing support', message: "Which students may need support?", icon: Users },
  { label: 'Review assignment performance', message: "How are students performing on assignments?", icon: FileCheck },
  { label: 'Generate quiz questions', message: "Generate practice quiz questions for this lesson.", icon: Brain },
  { label: 'Explain a concept', message: "Help me explain a concept in a simpler way.", icon: Lightbulb },
]

const FRIENDLY_TOOL_NAMES = {
  get_my_trainer_context: 'My Trainer Context',
  get_my_assigned_courses: 'My Assigned Courses',
  get_course_teaching_context: 'Course Teaching Context',
  get_my_student_support_list: 'Student Support List',
  get_student_teaching_context: 'Student Teaching Context',
  get_lesson_teaching_context: 'Lesson Teaching Context',
  get_assignment_teaching_context: 'Assignment Teaching Context',
  get_assignment_submission_context: 'Submission Context',
  get_trainer_daily_briefing: 'Daily Teaching Briefing',
}

export default function AITrainerCopilot({ preselectedCourseId, preselectedLessonId }) {
  const { user } = useAuthStore()
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [sessionId, setSessionId] = useState(null)
  const [courseId, setCourseId] = useState(preselectedCourseId || null)
  const [lessonId, setLessonId] = useState(preselectedLessonId || null)
  const [courses, setCourses] = useState([])
  const [showTrace, setShowTrace] = useState({})
  const messagesEndRef = useRef(null)

  // Load trainer's courses for context selection
  useEffect(() => {
    api.get('/trainers/courses')
      .then((res) => setCourses(res.data || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (preselectedCourseId) setCourseId(preselectedCourseId)
    if (preselectedLessonId) setLessonId(preselectedLessonId)
  }, [preselectedCourseId, preselectedLessonId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const handleSend = async (messageText) => {
    const text = (messageText || input).trim()
    if (!text || loading) return

    setInput('')
    setError(null)
    const userMsg = { role: 'user', content: text }
    setMessages((prev) => [...prev, userMsg])
    setLoading(true)

    try {
      const res = await api.post('/trainers/agent', {
        message: text,
        sessionId,
        courseId: courseId || undefined,
        lessonId: lessonId || undefined,
      })

      const data = res.data
      if (data.ok) {
        setSessionId(data.sessionId)
        const assistantMsg = {
          role: 'assistant',
          content: data.message?.content || '',
          toolCalls: data.message?.toolCalls || [],
          meta: data.meta,
        }
        setMessages((prev) => [...prev, assistantMsg])
      } else {
        setError(data.error || 'Something went wrong.')
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Request failed.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleQuickAction = (action) => {
    handleSend(action.message)
  }

  const toggleTrace = (idx) => {
    setShowTrace((prev) => ({ ...prev, [idx]: !prev[idx] }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-2">AI Copilot</span>
          <h1 className="font-display text-2xl sm:text-3xl text-white font-light flex items-center gap-3">
            <Sparkles size={24} className="text-cinema-gold" />
            Trainer Copilot
          </h1>
          <p className="font-sans text-sm text-cinema-muted max-w-xl mt-2">
            Your AI teaching assistant — grounded in your real LMS data. Prepare lessons, review student progress,
            generate quizzes, and get teaching recommendations.
          </p>
        </div>
      </div>

      {/* Context selector */}
      {courses.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-sans text-xs text-cinema-muted">Course context:</span>
          <select
            value={courseId || ''}
            onChange={(e) => {
              setCourseId(e.target.value || null)
              setLessonId(null)
            }}
            className="cinema-glass border border-white/8 px-3 py-1.5 text-sm font-sans text-cinema-soft rounded-sm bg-transparent"
          >
            <option value="">All courses</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>
      )}

      {/* Chat area */}
      <div className="cinema-glass border border-white/5 rounded-sm p-4 sm:p-6 min-h-[400px] max-h-[600px] overflow-y-auto">
        {messages.length === 0 && !loading && (
          <div className="text-center py-12">
            <Sparkles size={40} className="mx-auto mb-4 text-cinema-gold/30" />
            <p className="font-sans text-sm text-cinema-muted mb-1">Ask me anything about your teaching.</p>
            <p className="font-sans text-xs text-cinema-muted/70">I use your real LMS data to give grounded answers.</p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`mb-6 ${msg.role === 'user' ? '' : ''}`}>
            {msg.role === 'user' ? (
              <div className="flex justify-end">
                <div className="max-w-[80%] cinema-glass border border-cinema-gold/20 px-4 py-3 rounded-sm">
                  <p className="font-sans text-sm text-white whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ) : (
              <div className="flex justify-start">
                <div className="max-w-[85%]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Sparkles size={14} className="text-cinema-gold" />
                    <span className="font-sans text-xs text-cinema-muted">AI Copilot</span>
                  </div>
                  <div className="cinema-glass border border-white/5 px-4 py-3 rounded-sm">
                    <p className="font-sans text-sm text-cinema-soft whitespace-pre-wrap">{msg.content}</p>
                  </div>

                  {/* Tool trace */}
                  {msg.toolCalls && msg.toolCalls.length > 0 && (
                    <div className="mt-2">
                      <button
                        onClick={() => toggleTrace(idx)}
                        className="flex items-center gap-1 text-xs text-cinema-muted hover:text-cinema-gold font-sans transition-colors"
                      >
                        {showTrace[idx] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        How I reached this ({msg.toolCalls.length} tool{msg.toolCalls.length !== 1 ? 's' : ''})
                      </button>
                      {showTrace[idx] && (
                        <div className="mt-2 space-y-1 pl-4">
                          {msg.toolCalls.map((tc, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs font-sans text-cinema-muted">
                              <span className="w-1.5 h-1.5 rounded-full bg-cinema-glow/40" />
                              {FRIENDLY_TOOL_NAMES[tc.name] || tc.name}
                              {tc.durationMs && <span className="text-cinema-muted/50">· {tc.durationMs}ms</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex justify-start mb-6">
            <div className="flex items-center gap-2">
              <Loader2 size={16} className="animate-spin text-cinema-gold" />
              <span className="font-sans text-sm text-cinema-muted">Analyzing your teaching data...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 mb-6 p-3 border border-red-500/20 bg-red-500/5 rounded-sm">
            <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
            <p className="font-sans text-sm text-red-400">{error}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick actions */}
      {messages.length === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {QUICK_ACTIONS.map((action) => (
            <button
              key={action.label}
              onClick={() => handleQuickAction(action)}
              disabled={loading}
              className="cinema-glass border border-white/5 hover:border-cinema-gold/30 p-4 text-left rounded-sm transition-all group disabled:opacity-50"
            >
              <action.icon size={18} className="text-cinema-glow mb-2 group-hover:text-cinema-gold transition-colors" />
              <p className="font-sans text-sm text-cinema-soft">{action.label}</p>
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex gap-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
          placeholder="Ask about your courses, students, lessons, assignments..."
          disabled={loading}
          maxLength={1500}
          className="flex-1 cinema-glass border border-white/8 px-4 py-3 text-sm font-sans text-white placeholder:text-cinema-muted/50 rounded-sm focus:outline-none focus:border-cinema-gold/40 bg-transparent"
        />
        <button
          onClick={() => handleSend()}
          disabled={loading || !input.trim()}
          className="cinema-glass border border-cinema-gold/30 hover:border-cinema-gold/60 px-5 py-3 rounded-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Send size={16} className="text-cinema-gold" />
        </button>
      </div>
    </div>
  )
}
